import { c as _c } from "react/compiler-runtime";
// Редактор позиции загрузки в боковой панели: все поля черновика, варианты,
// фото из материалов, характеристики, описание с «Переписать», источник и
// проблемы позиции. На телефоне панель — на весь экран.
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, ExternalLink, ImagePlus, Loader2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { CatalogImportFile, CatalogImportItem, CatalogImportItemDraft, CatalogImportJobDetail, CatalogImportRegenerate } from "@osmix/contracts";
import { useRegenerateImportItem, useUpdateImportItem } from "@/api/hooks/use-catalog-import";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { cn } from "@/lib/utils";
import { CategorySelect } from "./category-select";
import { ImagePicker } from "./image-picker";
import { ImportThumb, IssueList, ItemStatusPill, errorText } from "./shared";

// ── Форма: числа держим строками, пока человек печатает ─────────────────

type VariantRow = {
  rid: string;
  size: string;
  russianSize: string;
  color: string;
  price: string;
  oldPrice: string;
  stock: string;
  barcode: string;
};
type CharRow = {
  rid: string;
  key: string;
  value: string;
};
type Form = {
  name: string;
  article: string;
  brand: string;
  categoryId: string | null;
  categoryPath?: string;
  color: string;
  composition: string;
  description: string;
  variants: VariantRow[];
  photoFileIds: string[];
  photoUrls: string[];
  chars: CharRow[];
  keywords: string;
  tags: string;
  madeToOrder: boolean;
  leadDays: string;
  dims: {
    lengthCm: string;
    widthCm: string;
    heightCm: string;
    weightG: string;
  };
};
let ridSeq = 0;
const rid = () => `r${++ridSeq}`;
const str = (n: number | null | undefined) => n == null ? "" : String(n);
const formOf = (d: CatalogImportItemDraft): Form => ({
  name: d.name,
  article: d.article,
  brand: d.brand,
  categoryId: d.categoryId,
  categoryPath: d.categoryPath,
  color: d.color,
  composition: d.composition ?? "",
  description: d.description,
  variants: d.variants.map(v => ({
    rid: rid(),
    size: v.size,
    russianSize: v.russianSize ?? "",
    color: v.color,
    price: str(v.price),
    oldPrice: str(v.oldPrice),
    stock: str(v.stock),
    barcode: v.barcode ?? ""
  })),
  photoFileIds: [...d.photoFileIds],
  photoUrls: [...d.photoUrls],
  chars: Object.entries(d.characteristics).map(([key, value]) => ({
    rid: rid(),
    key,
    value
  })),
  keywords: d.keywords.join(", "),
  tags: d.tags.join(", "),
  madeToOrder: !!d.madeToOrder,
  leadDays: str(d.madeToOrderLeadTimeDays),
  dims: {
    lengthCm: str(d.dimensions?.lengthCm),
    widthCm: str(d.dimensions?.widthCm),
    heightCm: str(d.dimensions?.heightCm),
    weightG: str(d.dimensions?.weightG)
  }
});

/** «1 490,5» → 1490.5; пусто → null; не число → NaN (поймает проверка). */
const num = (s: string): number | null => {
  const t = s.replace(/\s/g, "").replace(",", ".");
  return t === "" ? null : Number(t);
};
const list = (s: string) => [...new Set(s.split(",").map(x => x.trim()).filter(Boolean))];

/** Форма → черновик позиции; поля, которых в форме нет, — из исходника. */
const draftOf = (form: Form, base: CatalogImportItemDraft): CatalogImportItemDraft => {
  const dims = {
    lengthCm: num(form.dims.lengthCm) ?? undefined,
    widthCm: num(form.dims.widthCm) ?? undefined,
    heightCm: num(form.dims.heightCm) ?? undefined,
    weightG: num(form.dims.weightG) ?? undefined
  };
  const hasDims = Object.values(dims).some(v => v !== undefined);
  return {
    ...base,
    name: form.name.trim(),
    article: form.article.trim(),
    brand: form.brand.trim(),
    categoryId: form.categoryId,
    categoryPath: form.categoryPath,
    color: form.color.trim(),
    composition: form.composition.trim() || undefined,
    description: form.description.trim(),
    variants: form.variants.map(v => ({
      size: v.size.trim(),
      russianSize: v.russianSize.trim() || undefined,
      color: v.color.trim(),
      price: num(v.price),
      oldPrice: num(v.oldPrice),
      stock: num(v.stock),
      barcode: v.barcode.trim() || undefined
    })),
    photoFileIds: form.photoFileIds,
    photoUrls: form.photoUrls,
    characteristics: Object.fromEntries(form.chars.filter(c => c.key.trim()).map(c => [c.key.trim(), c.value.trim()])),
    keywords: list(form.keywords),
    tags: list(form.tags),
    madeToOrder: form.madeToOrder || undefined,
    madeToOrderLeadTimeDays: form.madeToOrder ? num(form.leadDays) ?? undefined : undefined,
    dimensions: hasDims ? dims : undefined
  };
};
const invalidNumbers = (draft: CatalogImportItemDraft): string | null => {
  for (const [i, v] of draft.variants.entries()) {
    const at = `вариант ${i + 1}`;
    if (v.price != null && (Number.isNaN(v.price) || v.price < 0)) return `Цена (${at}) — нужно число`;
    if (v.oldPrice != null && (Number.isNaN(v.oldPrice) || v.oldPrice < 0)) return `Старая цена (${at}) — нужно число`;
    if (v.stock != null && (!Number.isInteger(v.stock) || v.stock < 0)) return `Остаток (${at}) — целое число`;
  }
  for (const v of Object.values(draft.dimensions ?? {})) if (v != null && (Number.isNaN(v) || v < 0)) return "Габариты — нужны числа";
  if (draft.madeToOrderLeadTimeDays != null && !Number.isInteger(draft.madeToOrderLeadTimeDays)) return "Срок изготовления — целое число дней";
  return null;
};
const same = (a: CatalogImportItemDraft, b: CatalogImportItemDraft) => JSON.stringify(a) === JSON.stringify(b);
const textareaClass = "w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive md:text-sm";
const cellInput = "h-8 px-2 text-sm";

// ── Панель ───────────────────────────────────────────────────────────────

export function ItemEditor(t0) {
  const $ = _c(34);
  const {
    job,
    itemId,
    siblings,
    canEdit,
    onNavigate,
    onClose,
    onOpenFile
  } = t0;
  const item = job.items.find(i => i.id === itemId) ?? null;
  const dirty = useRef(false);
  const [pending, setPending] = useState(null);
  let t1;
  if ($[0] !== setPending) {
    t1 = action => {
      if (dirty.current) {
        setPending(() => action);
      } else {
        action();
      }
    };
    $[0] = setPending;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const guard = t1;
  const index = item ? siblings.indexOf(item.id) : -1;
  const prev = index > 0 ? siblings[index - 1] : undefined;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : undefined;
  const t2 = !!item;
  let t3;
  if ($[2] !== guard || $[3] !== onClose) {
    t3 = open => !open && guard(onClose);
    $[2] = guard;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== canEdit || $[6] !== guard || $[7] !== index || $[8] !== item || $[9] !== job || $[10] !== next || $[11] !== onNavigate || $[12] !== onOpenFile || $[13] !== prev || $[14] !== siblings) {
    t4 = item && <EditorBody key={item.id} job={job} item={item} canEdit={canEdit} position={index >= 0 ? `${index + 1} из ${siblings.length}` : `№ ${item.position}`} onPrev={prev ? () => guard(() => onNavigate(prev)) : undefined} onNext={next ? () => guard(() => onNavigate(next)) : undefined} onDirty={value => {
      dirty.current = value;
    }} onOpenFile={onOpenFile} />;
    $[5] = canEdit;
    $[6] = guard;
    $[7] = index;
    $[8] = item;
    $[9] = job;
    $[10] = next;
    $[11] = onNavigate;
    $[12] = onOpenFile;
    $[13] = prev;
    $[14] = siblings;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== t4) {
    t5 = <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-3xl" aria-describedby={undefined}>{t4}</SheetContent>;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t2 || $[19] !== t3 || $[20] !== t5) {
    t6 = <Sheet open={t2} onOpenChange={t3}>{t5}</Sheet>;
    $[18] = t2;
    $[19] = t3;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = !!pending;
  let t8;
  if ($[22] !== pending || $[23] !== setPending) {
    t8 = () => {
      const action_0 = pending;
      dirty.current = false;
      setPending(null);
      action_0?.();
    };
    $[22] = pending;
    $[23] = setPending;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== setPending) {
    t9 = () => setPending(null);
    $[25] = setPending;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <ConfirmDialog open={t7} title={"\u0412\u044B\u0439\u0442\u0438 \u0431\u0435\u0437 \u0441\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u0438\u044F?"} description={"\u041F\u0440\u0430\u0432\u043A\u0438 \u044D\u0442\u043E\u0439 \u043F\u043E\u0437\u0438\u0446\u0438\u0438 \u043D\u0435 \u0441\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u044B \u0438 \u043F\u0440\u043E\u043F\u0430\u0434\u0443\u0442."} confirmLabel={"\u041D\u0435 \u0441\u043E\u0445\u0440\u0430\u043D\u044F\u0442\u044C"} cancelLabel={"\u041E\u0441\u0442\u0430\u0442\u044C\u0441\u044F"} confirmVariant="destructive" onConfirm={t8} onClose={t9} />;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6) {
    t11 = <>{t6}{t10}</>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  return t11;
}
function EditorBody({
  job,
  item,
  canEdit,
  position,
  onPrev,
  onNext,
  onDirty,
  onOpenFile
}: {
  job: CatalogImportJobDetail;
  item: CatalogImportItem;
  canEdit: boolean;
  position: string;
  onPrev?: () => void;
  onNext?: () => void;
  onDirty: (dirty: boolean) => void;
  onOpenFile: (file: CatalogImportFile, locator?: string) => void;
}) {
  const update = useUpdateImportItem(job.id);
  const regenerate = useRegenerateImportItem(job.id);
  const [form, setForm] = useState<Form>(() => formOf(item.data));
  const [message, setMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [picking, setPicking] = useState(false);
  const [regen, setRegen] = useState<{
    instruction: string;
    fields: CatalogImportRegenerate["fields"];
  } | null>(null);
  const [regenError, setRegenError] = useState<string | null>(null);
  const editable = canEdit && item.status !== "committed" && (job.status === "review" || job.status === "done");
  const draft = draftOf(form, item.data);
  const isDirty = !same(draft, draftOf(formOf(item.data), item.data));
  useEffect(() => {
    onDirty(isDirty);
    return () => onDirty(false);
  }, [isDirty, onDirty]);
  const excluded = item.status === "excluded";
  const fieldIssue = (field: string) => item.issues.some(i => i.field === field && i.level === "error");
  const priceIssueRow = Number(item.issues.find(i_0 => i_0.field?.match(/^variants\.\d+\.price$/))?.field?.split(".")[1] ?? -1);
  const set = <K extends keyof Form,>(key: K, value: Form[K]) => {
    setMessage(null);
    setForm(f => ({
      ...f,
      [key]: value
    }));
  };
  const setVariant = (rowId: string, key_0: keyof VariantRow, value_0: string) => set("variants", form.variants.map(v => v.rid === rowId ? {
    ...v,
    [key_0]: value_0
  } : v));
  const setChar = (rowId_0: string, key_1: "key" | "value", value_1: string) => set("chars", form.chars.map(c => c.rid === rowId_0 ? {
    ...c,
    [key_1]: value_1
  } : c));
  const movePhoto = (from: number, to: number) => {
    const next = [...form.photoFileIds];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    set("photoFileIds", next);
  };
  const save = async (patch: {
    excluded?: boolean;
  } = {}) => {
    const bad = invalidNumbers(draft);
    if (bad) {
      setMessage({
        ok: false,
        text: bad
      });
      return;
    }
    setMessage(null);
    try {
      const saved = await update.mutateAsync({
        itemId: item.id,
        patch: {
          ...(isDirty ? {
            data: draft
          } : {}),
          ...patch
        }
      });
      setForm(formOf(saved.data));
      setMessage({
        ok: true,
        text: patch.excluded === undefined ? "Сохранено" : patch.excluded ? "Исключено из загрузки" : "Возвращено в загрузку"
      });
    } catch (e) {
      setMessage({
        ok: false,
        text: errorText(e)
      });
    }
  };
  const runRegenerate = async () => {
    if (!regen || regen.fields.length === 0) return;
    setRegenError(null);
    try {
      const fresh = await regenerate.mutateAsync({
        itemId: item.id,
        input: {
          fields: regen.fields,
          instruction: regen.instruction.trim() || undefined
        }
      });
      // Переписанное — с сервера, остальные несохранённые правки остаются.
      const next_0 = formOf(fresh.data);
      setForm(f_0 => ({
        ...f_0,
        ...(regen.fields.includes("description") ? {
          description: next_0.description
        } : {}),
        ...(regen.fields.includes("keywords") ? {
          keywords: next_0.keywords
        } : {}),
        ...(regen.fields.includes("characteristics") ? {
          chars: next_0.chars
        } : {}),
        ...(regen.fields.includes("category") ? {
          categoryId: next_0.categoryId,
          categoryPath: next_0.categoryPath
        } : {})
      }));
      setRegen(null);
    } catch (e_0) {
      setRegenError(errorText(e_0));
    }
  };
  const fileOf = (id: string) => job.files.find(f_1 => f_1.id === id);
  return <div className="flex h-full min-h-0 flex-col">
			<div className="flex items-start gap-2 border-b p-4 pr-12">
				<div className="min-w-0 flex-1">
					<SheetTitle className="truncate">{form.name || "Без названия"}</SheetTitle>
					<SheetDescription asChild>
						<div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
							<ItemStatusPill status={item.status} />
							<span>Позиция {position}</span>
							{item.data.confidence < 0.6 && <span className="text-amber-700 dark:text-amber-500">
									уверенность модели {Math.round(item.data.confidence * 100)} %
								</span>}
						</div>
					</SheetDescription>
				</div>
				<div className="flex shrink-0 gap-1">
					<Button variant="outline" size="icon-sm" disabled={!onPrev} onClick={onPrev} aria-label="Предыдущая позиция">
						<ChevronLeft className="size-4" />
					</Button>
					<Button variant="outline" size="icon-sm" disabled={!onNext} onClick={onNext} aria-label="Следующая позиция">
						<ChevronRight className="size-4" />
					</Button>
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto">
				<fieldset disabled={!editable} className="flex flex-col gap-6 p-4">
					{item.issues.length > 0 && <div className={cn("rounded-md border p-3", item.issues.some(i_1 => i_1.level === "error") ? "border-destructive/40 bg-destructive/5" : "border-amber-500/40 bg-amber-500/5")}>
							<IssueList issues={item.issues} />
						</div>}
					{item.status === "committed" && item.productId && <p className="rounded-md border bg-muted/40 p-3 text-sm">
							Позиция загружена в магазин.{" "}
							<Link to={`/products/${item.productId}`} className="text-primary hover:underline">
								Открыть товар
							</Link>
						</p>}
					{item.existingProductId && item.status !== "committed" && <p className="rounded-md border bg-muted/40 p-3 text-sm">
							Такой артикул уже есть в магазине — загрузка обновит{" "}
							<Link to={`/products/${item.existingProductId}`} className="text-primary hover:underline">
								этот товар
							</Link>
							, а не заведёт второй.
						</p>}

					<Section title={`Фото · ${form.photoFileIds.length + form.photoUrls.length}`}>
						<div className="flex flex-wrap gap-2">
							{form.photoFileIds.map((id_0, i_2) => {
              const file = fileOf(id_0);
              return <div key={id_0} className="group relative w-24">
										<button type="button" onClick={() => file && onOpenFile(file)} className="block w-full" title={file?.path || file?.filename || "Файл удалён"}>
											<ImportThumb jobId={job.id} fileId={file ? id_0 : null} className="aspect-[4/5] size-auto w-full rounded-md" />
										</button>
										{i_2 === 0 && <span className="absolute top-1 left-1 rounded bg-background/90 px-1 text-[10px] font-medium">Главное</span>}
										{editable && <div className="mt-1 flex justify-between">
												<Button type="button" variant="ghost" size="icon-xs" disabled={i_2 === 0} onClick={() => movePhoto(i_2, i_2 - 1)} aria-label="Сдвинуть фото влево">
													<ArrowLeft />
												</Button>
												<Button type="button" variant="ghost" size="icon-xs" onClick={() => set("photoFileIds", form.photoFileIds.filter(x => x !== id_0))} aria-label="Убрать фото">
													<Trash2 />
												</Button>
												<Button type="button" variant="ghost" size="icon-xs" disabled={i_2 === form.photoFileIds.length - 1} onClick={() => movePhoto(i_2, i_2 + 1)} aria-label="Сдвинуть фото вправо">
													<ArrowRight />
												</Button>
											</div>}
									</div>;
            })}
							{editable && <button type="button" onClick={() => setPicking(true)} className="flex aspect-[4/5] w-24 flex-col items-center justify-center gap-1 rounded-md border border-dashed text-center text-xs text-muted-foreground transition-colors hover:bg-muted/60">
									<ImagePlus className="size-5" />
									Из материалов
								</button>}
						</div>
						{form.photoUrls.length > 0 && <ul className="mt-2 flex flex-col gap-1 text-sm">
								{form.photoUrls.map(url => <li key={url} className="flex items-center gap-2">
										<span className="text-xs text-muted-foreground">по ссылке:</span>
										<a href={url} target="_blank" rel="noreferrer" className="min-w-0 truncate text-primary hover:underline">
											{url}
										</a>
										{editable && <Button type="button" variant="ghost" size="icon-xs" aria-label="Убрать ссылку на фото" onClick={() => set("photoUrls", form.photoUrls.filter(u => u !== url))}>
												<X />
											</Button>}
									</li>)}
								<li className="text-xs text-muted-foreground">Фото по ссылкам шлюз скачает при загрузке.</li>
							</ul>}
					</Section>

					<Section title="Основное">
						<div className="grid gap-3 sm:grid-cols-2">
							<Field label="Название" htmlFor="ie-name" className="sm:col-span-2">
								<Input id="ie-name" value={form.name} aria-invalid={fieldIssue("name") || undefined} onChange={e_1 => set("name", e_1.target.value)} />
							</Field>
							<Field label="Артикул" htmlFor="ie-article" hint="Пусто — сгенерируем при загрузке">
								<Input id="ie-article" value={form.article} onChange={e_2 => set("article", e_2.target.value)} />
							</Field>
							<Field label="Бренд" htmlFor="ie-brand">
								<Input id="ie-brand" value={form.brand} onChange={e_3 => set("brand", e_3.target.value)} />
							</Field>
							<Field label="Категория" htmlFor="ie-category" className="sm:col-span-2" hint={item.data.categoryHint ? `В материалах: «${item.data.categoryHint}»` : undefined}>
								<div className={cn("grid", fieldIssue("categoryId") && "[&_select]:border-destructive")}>
									<CategorySelect id="ie-category" value={form.categoryId} fallbackPath={form.categoryPath} onChange={(id_1, path) => setForm(f_2 => ({
                  ...f_2,
                  categoryId: id_1,
                  categoryPath: path
                }))} />
								</div>
							</Field>
							<Field label="Цвет" htmlFor="ie-color">
								<Input id="ie-color" value={form.color} onChange={e_4 => set("color", e_4.target.value)} />
							</Field>
							<Field label="Состав" htmlFor="ie-composition">
								<Input id="ie-composition" value={form.composition} onChange={e_5 => set("composition", e_5.target.value)} />
							</Field>
						</div>
					</Section>

					<Section title="Описание" action={editable && <Button type="button" variant="outline" size="sm" onClick={() => setRegen(r => r ? null : {
          instruction: "",
          fields: ["description"]
        })}>
									<Sparkles className="size-4" />
									Переписать
								</Button>}>
						{regen && <div className="mb-3 flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
								<Input value={regen.instruction} onChange={e_6 => setRegen({
              ...regen,
              instruction: e_6.target.value
            })} placeholder="Пожелание: «короче», «без слова премиум», «упомянуть карманы»" aria-label="Пожелание к переписыванию" className="h-9" />
								<div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
									{([["description", "Описание"], ["keywords", "Ключевые слова"], ["characteristics", "Характеристики"], ["category", "Категорию"]] as const).map(([key_2, label]) => <label key={key_2} className="flex items-center gap-1.5">
											<input type="checkbox" className="size-4" checked={regen.fields.includes(key_2)} onChange={e_7 => setRegen({
                  ...regen,
                  fields: e_7.target.checked ? [...regen.fields, key_2] : regen.fields.filter(f_3 => f_3 !== key_2)
                })} />
											{label}
										</label>)}
								</div>
								<div className="flex flex-wrap items-center gap-2">
									<Button type="button" size="sm" disabled={regen.fields.length === 0 || regenerate.isPending} onClick={() => void runRegenerate()}>
										{regenerate.isPending ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
										Переписать моделью
									</Button>
									<Button type="button" size="sm" variant="ghost" onClick={() => setRegen(null)}>
										Отмена
									</Button>
									<span className="text-xs text-muted-foreground">Пишет по фактам из материалов; тратит токены.</span>
								</div>
								{regenError && <p className="text-sm text-destructive">{regenError}</p>}
							</div>}
						<textarea id="ie-description" aria-label="Описание" value={form.description} aria-invalid={fieldIssue("description") || undefined} onChange={e_8 => set("description", e_8.target.value)} className={cn(textareaClass, "min-h-32")} />
					</Section>

					<Section title={`Варианты · ${form.variants.length}`} action={editable && <Button type="button" variant="outline" size="sm" onClick={() => set("variants", [...form.variants, {
          rid: rid(),
          size: "",
          russianSize: "",
          color: form.variants.at(-1)?.color ?? "",
          price: form.variants.at(-1)?.price ?? "",
          oldPrice: form.variants.at(-1)?.oldPrice ?? "",
          stock: "",
          barcode: ""
        }])}>
									<Plus className="size-4" />
									Добавить
								</Button>}>
						{form.variants.length === 0 ? <p className="text-sm text-muted-foreground">Вариантов нет — без цены позиция не загрузится.</p> : <>
								{/* Таблица — от планшета; на телефоне каждая строка карточкой. */}
								<div className="hidden overflow-x-auto sm:block">
									<table className="w-full min-w-[640px] text-sm">
										<thead>
											<tr className="text-left text-xs text-muted-foreground">
												<th className="pb-1 pr-1 font-normal">Размер</th>
												<th className="pb-1 pr-1 font-normal">Размер РФ</th>
												<th className="pb-1 pr-1 font-normal">Цвет</th>
												<th className="pb-1 pr-1 font-normal">Цена, ₽</th>
												<th className="pb-1 pr-1 font-normal">Старая, ₽</th>
												<th className="pb-1 pr-1 font-normal">Остаток</th>
												<th className="pb-1 pr-1 font-normal">Штрихкод</th>
												<th className="w-8" />
											</tr>
										</thead>
										<tbody>
											{form.variants.map((v_0, i_3) => <tr key={v_0.rid}>
													{(["size", "russianSize", "color", "price", "oldPrice", "stock", "barcode"] as const).map(key_3 => <td key={key_3} className="pr-1 pb-1">
															<Input value={v_0[key_3]} aria-label={`${VARIANT_LABELS[key_3]}, вариант ${i_3 + 1}`} inputMode={key_3 === "price" || key_3 === "oldPrice" || key_3 === "stock" ? "decimal" : undefined} aria-invalid={key_3 === "price" && i_3 === priceIssueRow && !v_0.price || undefined} onChange={e_9 => setVariant(v_0.rid, key_3, e_9.target.value)} className={cn(cellInput, (key_3 === "price" || key_3 === "oldPrice" || key_3 === "stock") && "text-right tabular-nums")} />
														</td>)}
													<td className="pb-1">
														{editable && <Button type="button" variant="ghost" size="icon-sm" aria-label={`Удалить вариант ${i_3 + 1}`} onClick={() => set("variants", form.variants.filter(x_0 => x_0.rid !== v_0.rid))}>
																<Trash2 className="size-4" />
															</Button>}
													</td>
												</tr>)}
										</tbody>
									</table>
								</div>
								<ul className="flex flex-col gap-2 sm:hidden">
									{form.variants.map((v_1, i_4) => <li key={v_1.rid} className="rounded-md border p-2">
											<div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
												Вариант {i_4 + 1}
												{editable && <Button type="button" variant="ghost" size="icon-xs" aria-label={`Удалить вариант ${i_4 + 1}`} onClick={() => set("variants", form.variants.filter(x_1 => x_1.rid !== v_1.rid))}>
														<Trash2 />
													</Button>}
											</div>
											<div className="grid grid-cols-2 gap-2">
												{(["size", "russianSize", "color", "price", "oldPrice", "stock", "barcode"] as const).map(key_4 => <label key={key_4} className={cn("text-xs text-muted-foreground", key_4 === "barcode" && "col-span-2")}>
														{VARIANT_LABELS[key_4]}
														<Input value={v_1[key_4]} inputMode={key_4 === "price" || key_4 === "oldPrice" || key_4 === "stock" ? "decimal" : undefined} aria-invalid={key_4 === "price" && i_4 === priceIssueRow && !v_1.price || undefined} onChange={e_10 => setVariant(v_1.rid, key_4, e_10.target.value)} className={cn(cellInput, "mt-0.5 text-foreground")} />
													</label>)}
											</div>
										</li>)}
								</ul>
							</>}
					</Section>

					<Section title="Характеристики" action={editable && <Button type="button" variant="outline" size="sm" onClick={() => set("chars", [...form.chars, {
          rid: rid(),
          key: "",
          value: ""
        }])}>
									<Plus className="size-4" />
									Добавить
								</Button>}>
						{form.chars.length === 0 ? <p className="text-sm text-muted-foreground">Характеристик нет.</p> : <ul className="flex flex-col gap-2">
								{form.chars.map(c_0 => <li key={c_0.rid} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] gap-2">
										<Input value={c_0.key} placeholder="Название" aria-label="Название характеристики" onChange={e_11 => setChar(c_0.rid, "key", e_11.target.value)} className={cellInput} />
										<Input value={c_0.value} placeholder="Значение" aria-label={`Значение: ${c_0.key || "характеристика"}`} onChange={e_12 => setChar(c_0.rid, "value", e_12.target.value)} className={cellInput} />
										{editable ? <Button type="button" variant="ghost" size="icon-sm" aria-label={`Удалить характеристику ${c_0.key}`} onClick={() => set("chars", form.chars.filter(x_2 => x_2.rid !== c_0.rid))}>
												<Trash2 className="size-4" />
											</Button> : <span />}
									</li>)}
							</ul>}
					</Section>

					<Section title="Поиск и доставка">
						<div className="grid gap-3 sm:grid-cols-2">
							<Field label="Ключевые слова" htmlFor="ie-keywords" hint="Через запятую">
								<Input id="ie-keywords" value={form.keywords} onChange={e_13 => set("keywords", e_13.target.value)} />
							</Field>
							<Field label="Теги" htmlFor="ie-tags" hint="Через запятую">
								<Input id="ie-tags" value={form.tags} onChange={e_14 => set("tags", e_14.target.value)} />
							</Field>
							<div className="grid grid-cols-4 gap-2 sm:col-span-2">
								{([["lengthCm", "Длина, см"], ["widthCm", "Ширина, см"], ["heightCm", "Высота, см"], ["weightG", "Вес, г"]] as const).map(([key_5, label_0]) => <label key={key_5} className="min-w-0 text-xs text-muted-foreground">
										{label_0}
										<Input value={form.dims[key_5]} inputMode="decimal" onChange={e_15 => set("dims", {
                  ...form.dims,
                  [key_5]: e_15.target.value
                })} className={cn(cellInput, "mt-0.5 text-foreground")} />
									</label>)}
							</div>
							<label className="flex items-center gap-2 text-sm">
								<input type="checkbox" className="size-4" checked={form.madeToOrder} onChange={e_16 => set("madeToOrder", e_16.target.checked)} />
								Под заказ
							</label>
							{form.madeToOrder && <Field label="Срок изготовления, дней" htmlFor="ie-lead">
									<Input id="ie-lead" inputMode="numeric" value={form.leadDays} onChange={e_17 => set("leadDays", e_17.target.value)} />
								</Field>}
						</div>
					</Section>

					<Section title="Источник">
						{item.data.sourceRefs.length === 0 ? <p className="text-sm text-muted-foreground">Модель не указала, откуда взяла позицию.</p> : <ul className="flex flex-col gap-1 text-sm">
								{item.data.sourceRefs.map((ref, i_5) => {
              const file_0 = fileOf(ref.fileId);
              return <li key={`${ref.fileId}-${i_5}`} className="flex flex-wrap items-center gap-x-2">
											<span className="min-w-0 truncate">{file_0 ? file_0.path || file_0.filename : "файл удалён"}</span>
											{ref.locator && <span className="text-muted-foreground">· {ref.locator}</span>}
											{file_0 && <button type="button" onClick={() => onOpenFile(file_0, ref.locator)} className="inline-flex items-center gap-1 text-primary hover:underline">
													<ExternalLink className="size-3.5" />
													Открыть
												</button>}
										</li>;
            })}
							</ul>}
					</Section>
				</fieldset>
			</div>

			{editable && <div className="flex flex-col gap-2 border-t p-3 sm:flex-row sm:items-center">
					<Button type="button" variant="ghost" className={cn("sm:mr-auto", !excluded && "text-destructive")} disabled={update.isPending} onClick={() => void save({
        excluded: !excluded
      })}>
						{excluded ? "Вернуть в загрузку" : "Исключить из загрузки"}
					</Button>
					{message && <span role="status" className={cn("text-sm", message.ok ? "text-muted-foreground" : "text-destructive")}>
							{message.text}
						</span>}
					<div className="flex gap-2">
						<Button type="button" variant="outline" className="flex-1 sm:flex-none" disabled={!isDirty || update.isPending} onClick={() => {
          setForm(formOf(item.data));
          setMessage(null);
        }}>
							Отменить правки
						</Button>
						<Button type="button" className="flex-1 sm:flex-none" disabled={!isDirty || update.isPending} onClick={() => void save()}>
							{update.isPending && <Loader2 className="size-4 animate-spin" />}
							Сохранить
						</Button>
					</div>
				</div>}

			<ImagePicker open={picking} jobId={job.id} files={job.files} exclude={form.photoFileIds} title="Фото к позиции" description="Выберите картинки из материалов задания — добавятся в конец, первое фото остаётся главным." onConfirm={ids => set("photoFileIds", [...form.photoFileIds, ...ids].slice(0, 30))} onClose={() => setPicking(false)} />
		</div>;
}
const VARIANT_LABELS: Record<Exclude<keyof VariantRow, "rid">, string> = {
  size: "Размер",
  russianSize: "Размер РФ",
  color: "Цвет",
  price: "Цена, ₽",
  oldPrice: "Старая цена, ₽",
  stock: "Остаток",
  barcode: "Штрихкод"
};
function Section(t0) {
  const $ = _c(8);
  const {
    title,
    action,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-sm font-semibold">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== action || $[3] !== t1) {
    t2 = <div className="flex items-center justify-between gap-2">{t1}{action}</div>;
    $[2] = action;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <section className="flex flex-col gap-2">{t2}{children}</section>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function Field(t0) {
  const $ = _c(12);
  const {
    label,
    htmlFor,
    hint,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("min-w-0 space-y-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== htmlFor || $[3] !== label) {
    t2 = <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">{label}</label>;
    $[2] = htmlFor;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== hint) {
    t3 = hint && <p className="text-xs text-muted-foreground">{hint}</p>;
    $[5] = hint;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className={t1}>{t2}{children}{t3}</div>;
    $[7] = children;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}